import { NodeResizerProps, useReactFlow } from '@xyflow/react';
import { useCallback } from 'react';

import { diagnostics } from '@/utils/diagnostics';

import { useSelectedNodes } from './useSelectedNodes';

/**
 * @description This hook is used to resize all selected nodes relative to a single node, provided to the onResize prop of the NodeResizer component.
 */
export function useResizeAllSelectedNodes(id: string) {
  const { updateNode } = useReactFlow();
  const { getSelectedNodes } = useSelectedNodes();

  return useCallback<NonNullable<NodeResizerProps['onResize']>>(
    (_event, { width, height, x, y }) => {
      const resizingNode = getSelectedNodes().find((node) => node.id === id);
      const nonResizingNodes = getSelectedNodes().filter(
        (node) => node.id !== id
      );

      // Only do this if there are other nodes selected
      if (!nonResizingNodes.length) {
        return;
      }

      // Should never happen in theory
      if (!resizingNode) {
        diagnostics.error(
          `No resizing node found for id: ${id}, likely a reactflow issue. Skipping resize.`
        );
        return;
      }

      // First, always update the resizing node to the new position / dimensions
      updateNode(resizingNode.id, {
        width,
        height,
        position: { x, y },
      });

      const xChange = x - resizingNode.position.x;
      const yChange = y - resizingNode.position.y;

      // Then, update all other selected nodes to maintain their relative position to the resizing node.
      // Only nodes of the same type follow: applying e.g. a relationship
      // label's dimensions to a tile (or vice versa) would distort it.
      nonResizingNodes.forEach((node) => {
        if (node.type !== resizingNode.type) return;

        updateNode(node.id, {
          width,
          height,
          position: {
            x: node.position.x + xChange,
            y: node.position.y + yChange,
          },
        });
      });
    },
    [getSelectedNodes, id, updateNode]
  );
}
